export interface NotificationData {
  event: string;
  entityType: 'production' | 'sample' | 'express_order' | 'express_sample';
  entityId: string;
  entityName: string;
  status?: string;
  clientName?: string;
  salesperson?: string;
  deliveryDate?: string;
  timestamp: string;
}

/**
 * Envía una notificación a Make (Integromat)
 */
export const sendNotificationToMake = async (data: NotificationData): Promise<void> => {
  try {
    // URL del webhook de Make (configurar en Make)
    const makeWebhookUrl =
      (typeof import.meta !== 'undefined' ? (import.meta as any).env?.VITE_MAKE_WEBHOOK_URL : undefined) ||
      (typeof window !== 'undefined' ? (window as any).VITE_MAKE_WEBHOOK_URL : undefined) ||
      'https://hook.eu1.make.com/YOUR_WEBHOOK_ID';
    
    if (makeWebhookUrl === 'https://hook.eu1.make.com/YOUR_WEBHOOK_ID') {
      console.warn('Make webhook URL no configurada. Configura VITE_MAKE_WEBHOOK_URL en tu .env');
      return;
    }

    const response = await fetch(makeWebhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Error al enviar notificación: ${response.status}`);
    }

    console.log('Notificación enviada exitosamente a Make:', data);
  } catch (error) {
    console.error('Error al enviar notificación a Make:', error);
    // No lanzamos el error para no interrumpir el flujo principal
  }
};

/**
 * Notifica cuando se crea una nueva entidad
 */
export const notifyEntityCreated = async (
  entityType: 'production' | 'sample' | 'express_order' | 'express_sample',
  entity: any,
  clientName?: string
) => {
  const notificationData: NotificationData = {
    event: 'entity_created',
    entityType,
    entityId: entity.id,
    entityName: entity.name,
    status: entity.status,
    clientName,
    salesperson: entity.salesperson,
    deliveryDate: entity.delivery_date,
    timestamp: new Date().toISOString(),
  };

  await sendNotificationToMake(notificationData);
};

/**
 * Notifica cuando una entidad cambia a estado "finished"
 */
export const notifyEntityFinished = async (
  entityType: 'production' | 'sample' | 'express_order' | 'express_sample',
  entity: any,
  clientName?: string
) => {
  const notificationData: NotificationData = {
    event: 'entity_finished',
    entityType,
    entityId: entity.id,
    entityName: entity.name,
    status: entity.status,
    clientName,
    salesperson: entity.salesperson,
    deliveryDate: entity.delivery_date,
    timestamp: new Date().toISOString(),
  };

  await sendNotificationToMake(notificationData);
};

/**
 * Notifica cuando se actualiza una entidad
 */
export const notifyEntityUpdated = async (
  entityType: 'production' | 'sample' | 'express_order' | 'express_sample',
  entity: any,
  clientName?: string,
  previousStatus?: string
) => {
  const notificationData: NotificationData = {
    event: 'entity_updated',
    entityType,
    entityId: entity.id,
    entityName: entity.name,
    status: entity.status,
    clientName,
    salesperson: entity.salesperson,
    deliveryDate: entity.delivery_date,
    timestamp: new Date().toISOString(),
  };

  await sendNotificationToMake(notificationData);

  // Si el estado cambió a "finished", enviar notificación adicional
  if (entity.status === 'finished' && previousStatus !== 'finished') {
    await notifyEntityFinished(entityType, entity, clientName);
  }
}; 